import { useEffect, useMemo, useRef, useState } from 'react';

const CITE_BADGE = /<cite-badge data-idx="\d+"><\/cite-badge>/g;
const WORDS_PER_SECOND = 5; // Gentler pace (down from 7)
const PUNCTUATION_PAUSE_MS = 150; // Brief pause after sentence-ending punctuation
const SENTENCE_END_CHARS = /[.!?;:]/;

/**
 * Smooth streaming hook - releases text word-by-word at controlled rate.
 * Citation-aware: treats <cite-badge> tags as atomic units.
 * Includes natural rhythm with micro-pauses after punctuation.
 */
export function useSmoothStream(rawText: string, isStreaming: boolean): string {
  // Seed from the current rawText so a mid-stream remount (e.g. navigating
  // away from an Evidence question and back) resumes from the already-streamed
  // text instead of replaying it from the start. Fresh streams mount with an
  // empty rawText, so this is a no-op for them.
  const [displayedText, setDisplayedText] = useState(rawText);
  const indexRef = useRef(rawText.length);
  const lastTimeRef = useRef(performance.now());
  const rafRef = useRef<number>(0);
  const pauseUntilRef = useRef<number>(0); // Track punctuation pause timing

  // Precompute citation boundaries
  const citations = useMemo(() => {
    const bounds: { start: number; end: number }[] = [];
    const re = new RegExp(CITE_BADGE.source, 'g');
    let match;
    while ((match = re.exec(rawText))) {
      bounds.push({ start: match.index, end: match.index + match[0].length });
    }
    return bounds;
  }, [rawText]);

  // Sync state when not streaming OR reset when text replaced with shorter content
  useEffect(() => {
    if (!isStreaming) {
      // Not streaming: show full text immediately
      setDisplayedText(rawText);
      indexRef.current = rawText.length;
    } else if (indexRef.current > rawText.length) {
      // Streaming but text got shorter (new conversation): reset animation
      indexRef.current = 0;
      setDisplayedText('');
      lastTimeRef.current = performance.now();
    }
  }, [rawText, isStreaming]);

  // Animation loop
  useEffect(() => {
    if (!isStreaming || indexRef.current >= rawText.length) return;

    const animate = () => {
      const now = performance.now();

      // Check if we're in a punctuation pause
      if (now < pauseUntilRef.current) {
        rafRef.current = requestAnimationFrame(animate);
        return;
      }

      const elapsed = now - lastTimeRef.current;
      const charsToAdd = Math.floor((elapsed / 1000) * WORDS_PER_SECOND * 5);

      if (charsToAdd > 0) {
        let target = indexRef.current + charsToAdd;

        // Extend to word boundary first
        while (target < rawText.length && !/\s/.test(rawText[target]!)) {
          target++;
        }

        // Don't split citation tags - check AFTER word boundary extension
        for (const { start, end } of citations) {
          if (target > start && target < end) target = end;
        }

        target = Math.min(target, rawText.length);
        if (target > indexRef.current) {
          // Check if we're ending on punctuation - trigger a micro-pause
          const lastChar = rawText[target - 1];
          const isPunctuation = lastChar && SENTENCE_END_CHARS.test(lastChar);

          indexRef.current = target;
          setDisplayedText(rawText.slice(0, target));
          lastTimeRef.current = now;

          // Add natural pause after sentence-ending punctuation
          if (isPunctuation) {
            pauseUntilRef.current = now + PUNCTUATION_PAUSE_MS;
          }
        }
      }

      if (indexRef.current < rawText.length) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [rawText, citations, isStreaming]);

  return displayedText;
}
